/**
 * The Ready screen (plan §23.4, §18.1).
 *
 *   "Your Construct is ready. Choose how you want to use it."
 *
 * The primary CTA is deliberately neutral, and Hark is ONE CARD among several — this screen is
 * where the product makes clear that Hark is an option, not the platform. Cards, in order:
 * Connect Hark, Connect an AI agent (MCP), Import existing conversations, Install browser
 * capture, Create an API credential, Explore data & portability.
 *
 * §18.1: high-level state only. No bucket, no repo, no schema, no provider name.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError, api } from "../api/client.js";
import { Banner, CodeBlock, ErrorNote, Loading, StatusPill, TokenModal, formatDate, formatDay } from "../components/ui.js";
import { useSession } from "../state/session.js";
import type { DeploymentConnectionsSummary, IssuedConnection } from "../api/types.js";

/** The example an agent author can paste before they have a key (§12.5). */
function exampleMcpConfig(mcpUrl: string): string {
  return JSON.stringify(
    {
      mcpServers: {
        construct: {
          type: "http",
          url: mcpUrl,
          headers: { Authorization: "Bearer <your-construct-key>" },
        },
      },
    },
    null,
    2,
  );
}

export default function Dashboard() {
  const { me, refresh } = useSession();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<DeploymentConnectionsSummary | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [issued, setIssued] = useState<IssuedConnection | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setSummary(await api.deploymentConnections());
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!me) return null;
  const deployment = me.deployment;

  // A signed-in account with no Construct is valid — offer the ordinary path, don't error.
  if (!deployment) {
    return (
      <>
        <div className="page-head">
          <h1>You do not have a Construct yet</h1>
          <p className="lede">Choose a plan and we will build one for you. It takes a couple of minutes.</p>
        </div>
        <div className="btn-row">
          <Link className="btn btn--primary" to="/plans">
            Get started
          </Link>
        </div>
      </>
    );
  }

  if (loading) return <Loading label="Loading your Construct" />;

  const endpoint = summary && "endpoint" in summary ? summary.endpoint : null;
  const clients = summary && !Array.isArray(summary.clients) ? summary.clients : null;
  const ready = deployment.status === "READY";

  async function createAgentCredential() {
    setBusy("agent");
    try {
      const result = await api.createConnection({
        displayName: "AI agent",
        clientType: "agent",
        capabilityProfile: "agent-recall",
      });
      setIssued(result);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  async function createApiCredential() {
    setBusy("owner");
    try {
      const result = await api.createConnection({
        displayName: "API credential",
        clientType: "owner",
        capabilityProfile: "owner-full",
      });
      setIssued(result);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="page-head">
        <p className="eyebrow">Your Construct</p>
        <h1>{ready ? "Your Construct is ready." : "Your Construct"}</h1>
        {ready ? <p className="lede">Choose how you want to use it.</p> : null}
        <div className="row" style={{ marginTop: 18 }}>
          <StatusPill status={deployment.status} />
          <span className="mono small muted">Last verified {formatDate(deployment.lastVerifiedAt)}</span>
        </div>
      </div>

      {error ? <ErrorNote error={error} onRetry={() => void load()} /> : null}

      {deployment.status === "SUSPENDED" ? (
        <Banner kind="warn" title="Your service is paused">
          A payment did not go through, so ingest and agent access are paused.{" "}
          <strong>Your data is safe and you can still export it.</strong> Update your payment
          details to switch everything back on.{" "}
          <Link to="/billing">Go to billing</Link>
        </Banner>
      ) : null}

      {me.entitlement.reason === "grace_period" ? (
        <Banner kind="warn" title="We could not take your last payment">
          Everything still works for now. Update your card to avoid interruption.{" "}
          <Link to="/billing">Fix billing</Link>
        </Banner>
      ) : null}

      {deployment.status === "CANCELLATION_PENDING" ? (
        <Banner kind="warn" title="Your service ends soon">
          Your Construct stays available until {formatDay(me.entitlement.currentPeriodEnd)}, and
          your data is kept afterwards so you can export or reactivate.{" "}
          <Link to="/data">Export your data</Link>
        </Banner>
      ) : null}

      {deployment.status === "RETAINED" ? (
        <Banner kind="warn" title="Your service has ended">
          Your data is kept until {formatDay(deployment.retentionExpiresAt)}. You can export it, or
          reactivate to pick up exactly where you left off. <Link to="/billing">Reactivate</Link>
        </Banner>
      ) : null}

      {ready && endpoint ? (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card__head">
            <div>
              <h3>Your endpoint</h3>
              <p className="mono muted" style={{ marginTop: 6 }}>
                {endpoint.mcpUrl}
              </p>
            </div>
            <span className="pill pill--ok">Live</span>
          </div>
        </div>
      ) : null}

      <div className="grid">
        {/* ---- Connect Hark ------------------------------------------------ */}
        <div className="card">
          <div className="card__head">
            <h3>Connect Hark</h3>
            {clients?.hark ? <span className="pill pill--ok">Connected</span> : <span className="pill pill--idle">Optional</span>}
          </div>
          <div className="card__body">
            Hark is our companion app for capturing things on your phone. It is one way to use
            your Construct, entirely optional.
          </div>
          <div className="btn-row">
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={!ready}
              onClick={() => navigate("/pair")}
            >
              {clients?.hark ? "Pair another device" : "Pair a device"}
            </button>
          </div>
        </div>

        {/* ---- Connect an AI agent (MCP) ----------------------------------- */}
        <div className="card">
          <div className="card__head">
            <h3>Connect an AI agent</h3>
            {clients?.mcp ? <span className="pill pill--ok">Connected</span> : null}
          </div>
          <div className="card__body">
            Let Claude, or any MCP-speaking agent, recall from your Construct. Give it your endpoint
            address and it asks you to sign in and approve what it may do. There is no key to copy.
          </div>
          {endpoint ? (
            <div style={{ marginTop: 14 }}>
              <CodeBlock code={exampleMcpConfig(endpoint.mcpUrl)} label="Copy example" />
            </div>
          ) : null}
          <div className="btn-row">
            <Link className="btn btn--primary btn--sm" to="/link-agent">
              Link an agent
            </Link>
            {/* The manual lane survives for harnesses that only take a fixed header, but it is no
                longer the front door: it hands the customer a bearer secret to look after. */}
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={!ready || busy !== null}
              onClick={() => void createAgentCredential()}
            >
              {busy === "agent" ? "Creating" : "Create a key instead"}
            </button>
            <Link className="btn btn--ghost btn--sm" to="/connections">
              Manage connections
            </Link>
          </div>
        </div>

        {/* ---- Import existing conversations ------------------------------- */}
        <div className="card">
          <div className="card__head">
            <h3>Import conversations</h3>
            <span className="pill pill--idle">Coming soon</span>
          </div>
          <div className="card__body">
            Bring in what you already have, from chat exports to notes and documents, so recall works
            from day one.
          </div>
          <div className="btn-row">
            <button type="button" className="btn btn--ghost btn--sm" disabled>
              Import
            </button>
          </div>
        </div>

        {/* ---- Browser capture --------------------------------------------- */}
        <div className="card">
          <div className="card__head">
            <h3>Install browser capture</h3>
            {clients?.browserExtension ? (
              <span className="pill pill--ok">Connected</span>
            ) : (
              <span className="pill pill--idle">Coming soon</span>
            )}
          </div>
          <div className="card__body">
            Save pages and images straight into your Construct as you browse.
          </div>
          <div className="btn-row">
            <button type="button" className="btn btn--ghost btn--sm" disabled>
              Install
            </button>
          </div>
        </div>

        {/* ---- Create an API credential ------------------------------------ */}
        <div className="card">
          <div className="card__head">
            <h3>Create an API credential</h3>
          </div>
          <div className="card__body">
            Building something yourself? Create a key with full access to your own Construct. It
            is shown once.
          </div>
          <div className="btn-row">
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={!ready || busy !== null}
              onClick={() => void createApiCredential()}
            >
              {busy === "owner" ? "Creating" : "Create credential"}
            </button>
          </div>
        </div>

        {/* ---- Data & portability ------------------------------------------ */}
        <div className="card card--accent">
          <div className="card__head">
            <h3>Your data</h3>
          </div>
          {/*
            §14.1: the export guarantee belongs HERE, on the Ready screen, not only on /data. A
            promise about leaving is worth most on day one, before there is anything to lose, and
            the concrete facts are what make it a promise rather than a slogan.
          */}
          <div className="card__body">
            <strong>Your data is yours from day one.</strong> Export everything at any time, in an
            open format: your vault with its full Git history, every file you have stored, and your
            structured data, with checksums so you can verify it without us. Cancelling never
            deletes anything, and deleting is always something you choose deliberately.
          </div>
          <div className="btn-row">
            <Link className="btn btn--ghost btn--sm" to="/data">
              Explore data &amp; portability
            </Link>
          </div>
        </div>
      </div>

      {issued ? (
        <TokenModal
          title={issued.connection.displayName}
          token={issued.token}
          mcpConfig={issued.config}
          endpointUrl={issued.endpoint.mcpUrl}
          onClose={() => {
            setIssued(null);
            void refresh();
          }}
        />
      ) : null}
    </>
  );
}

export { ApiError };
